// Pure, dependency-free merge for allLocalesStore values.
//
// WHY MERGE INSTEAD OF REPLACE:
// Every writer of allLocalesStore used to REPLACE locales.regions wholesale. The
// background hierarchy crawl (fetchRemainingLocationsInBackground) builds its result
// from a snapshot of the regions captured when it STARTED and never re-reads the store;
// when it finally writes at the end, it overwrites the whole tree — silently discarding
// any on-demand subtree that was fetched and written WHILE the crawl was running. Two
// supplier instances (or a Turbo navigation mid-crawl) make this worse: staggered writes
// clobber each other. Routing every write through this merge means a late-landing write
// unions into — rather than replaces — whatever is already in the store.
//
// The function is pure (never mutates either argument) and returns a brand-new value so
// it is trivially unit-testable.

// Child-array key for each node level. A room has no children.
const CHILDREN_KEY = {
    region: 'buildings',
    building: 'areas',
    area: 'rooms'
}

const CHILD_TYPE = {
    region: 'building',
    building: 'area',
    area: 'room'
}

export function mergeLocaleHierarchy(currentValue, incomingValue) {
    const current = currentValue || {}
    const incoming = incomingValue || {}

    const currentRegions = current.locales?.regions || []
    const incomingRegions = incoming.locales?.regions || []

    return {
        // FLAGS are monotonic: once true they must never flip back to false. A second or
        // staggered crawl's regions-only write carries initialLoadComplete: false, which
        // must not re-block consumers after an earlier crawl already completed.
        regionsLoaded: !!(current.regionsLoaded || incoming.regionsLoaded),
        initialLoadComplete: !!(current.initialLoadComplete || incoming.initialLoadComplete),
        // Prefer incoming's refresh timestamp when it supplied one; otherwise keep current's.
        lastRefreshTime: incoming.lastRefreshTime != null ? incoming.lastRefreshTime : current.lastRefreshTime,
        locales: {
            regions: mergeNodeArrays(currentRegions, incomingRegions, 'region')
        }
    }
}

// Union two node arrays by id, preserving current's order first and appending any
// incoming-only nodes. Nodes present in both are deep-merged per mergeNode.
function mergeNodeArrays(currentNodes, incomingNodes, nodeType) {
    const incomingById = new Map()
    for (const node of incomingNodes) {
        incomingById.set(node.id, node)
    }

    const merged = []
    const seen = new Set()

    // Current's nodes first (stable order), merged with any incoming match.
    for (const currentNode of currentNodes) {
        const incomingNode = incomingById.get(currentNode.id)
        merged.push(incomingNode ? mergeNode(currentNode, incomingNode, nodeType) : currentNode)
        seen.add(currentNode.id)
    }

    // Then any nodes that exist only in incoming, appended in incoming's order.
    for (const incomingNode of incomingNodes) {
        if (!seen.has(incomingNode.id)) {
            merged.push(incomingNode)
        }
    }

    return merged
}

// Merge a single node present on BOTH sides: start from current, overlay incoming's
// non-null scalar fields, merge the children array, and resolve childrenLoadFailed.
function mergeNode(currentNode, incomingNode, nodeType) {
    const merged = { ...currentNode }

    // Overlay incoming's scalar fields only when it actually supplied a value — never let
    // a missing/null incoming field blank out something current already has.
    for (const key of Object.keys(incomingNode)) {
        if (key === CHILDREN_KEY[nodeType] || key === 'childrenLoadFailed') {
            continue // handled explicitly below
        }
        if (incomingNode[key] != null) {
            merged[key] = incomingNode[key]
        }
    }

    const childrenKey = CHILDREN_KEY[nodeType]

    if (childrenKey) {
        const currentChildren = currentNode[childrenKey] || []
        const incomingChildren = incomingNode[childrenKey] || []
        merged[childrenKey] = mergeChildren(currentChildren, incomingChildren, CHILD_TYPE[nodeType])
    }

    // childrenLoadFailed resolution:
    // - If the node ended up WITH children, the fetch clearly succeeded: drop the marker.
    // - Else, incoming wins IF it supplied this node at all (present-or-absent). This is
    //   deliberate: clearNodeChildrenLoadFailed intentionally writes the node with NO
    //   marker and empty children to record "genuinely empty". Merely preserving current's
    //   marker would undo that intentional clear, so incoming must win for this one field.
    //   If incoming did not touch this node, keep current's marker.
    const mergedChildren = childrenKey ? merged[childrenKey] : null
    if (mergedChildren && mergedChildren.length > 0) {
        delete merged.childrenLoadFailed
    } else if (incomingNode.childrenLoadFailed) {
        merged.childrenLoadFailed = true
    } else {
        // incoming supplied this node (it's the match side) but with no marker -> take that
        // cleared state. (If incoming had the marker we'd have hit the branch above.)
        delete merged.childrenLoadFailed
    }

    return merged
}

// Merge two child arrays. An EMPTY array means "not loaded / unknown", NOT "known empty",
// so an empty side must never erase a non-empty one.
function mergeChildren(currentChildren, incomingChildren, childType) {
    const currentHas = currentChildren.length > 0
    const incomingHas = incomingChildren.length > 0

    if (currentHas && incomingHas) {
        return mergeNodeArrays(currentChildren, incomingChildren, childType)
    }
    if (currentHas) {
        return currentChildren
    }
    if (incomingHas) {
        return incomingChildren
    }
    return []
}
